/**
 * NotificationRow — tek bildirim satırı.
 *
 * Nested-interactive anti-pattern düzeltmesi: satır tek büyük <button> DEĞİL,
 * iki KARDEŞ interaktif eleman içeren bir <div>:
 *   (a) ana içerik butonu — tıkla → okundu işaretle + deeplink'e git
 *   (b) sadece unread'de görünen ayrı "tekil okundu işaretle" ikon-butonu
 *       (navigasyon YOK, sadece okundu işaretler)
 */
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronRight } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { resolveIcon, resolveToneStyles } from './notificationMeta';
import { resolveNavigationTarget } from './notificationNav';
import { formatRelativeTime, formatAbsoluteDateTime, toIsoString } from '../../utils/relativeTime';

export default function NotificationRow({
  notif,
  onMarkRead,
  onActivate,
  rowRef,
  tabIndex,
  onFocusRow,
}) {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const isUnread = !notif.is_read;
  const Icon = resolveIcon(notif);
  const styles = resolveToneStyles(notif);

  const handleActivate = useCallback(() => {
    if (isUnread) onMarkRead(notif.notification_id);
    // Kırık /announcements/{id} fallback deeplink'ine düşme regresyonunun asıl
    // fix'i App.js'teki /announcements/:id redirect route'u — bkz.
    // notificationNav.js üstündeki güncellenmiş yorum (has_cta kontrolü şu an
    // deeplink'ten bağımsız bir şey filtrelemiyor, ileriye dönük savunma).
    const target = resolveNavigationTarget(notif);
    if (target) {
      try {
        const url = new URL(target);
        navigate(url.pathname + url.search + url.hash);
      } catch {
        // Göreli path
        navigate(target);
      }
    }
    if (onActivate) onActivate();
  }, [isUnread, notif, onMarkRead, navigate, onActivate]);

  const handleMarkReadOnly = useCallback(
    (e) => {
      e.stopPropagation();
      onMarkRead(notif.notification_id);
    },
    [notif.notification_id, onMarkRead]
  );

  const timeLabel = formatRelativeTime(notif.created_at, lang);
  const fullDate = formatAbsoluteDateTime(notif.created_at, lang);
  const isoDate = toIsoString(notif.created_at);

  return (
    <div
      className={`flex items-stretch gap-0 rounded-xl border-l-4 transition-colors ${
        isUnread ? `${styles.unreadBg} ${styles.border}` : 'bg-white border-transparent'
      }`}
    >
      <button
        type="button"
        ref={rowRef}
        tabIndex={tabIndex}
        onFocus={onFocusRow}
        onClick={handleActivate}
        className={`flex-1 min-w-0 flex items-start gap-3 px-3 py-2.5 min-h-[44px] text-left rounded-xl transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] ${
          isUnread ? 'hover:brightness-95' : 'hover:bg-gray-50'
        }`}
        aria-label={notif.title}
      >
        <span
          className={`shrink-0 w-9 h-9 rounded-xl flex items-center justify-center mt-0.5 ${
            isUnread ? `${styles.chipBg} ${styles.chipText}` : 'bg-gray-100 text-gray-400'
          }`}
        >
          <Icon size={18} aria-hidden="true" />
        </span>

        <span className="flex-1 min-w-0">
          <span
            className={`text-sm leading-tight line-clamp-1 ${
              isUnread ? 'font-bold text-[var(--text-main)]' : 'font-semibold text-[var(--text-main)]'
            }`}
          >
            {notif.title}
          </span>
          {notif.body && (
            <span className="text-xs text-[var(--text-secondary)] line-clamp-2 mt-0.5 leading-relaxed">
              {notif.body}
            </span>
          )}
          <span className="flex items-center gap-2 mt-1 flex-wrap">
            {timeLabel && (
              <time
                dateTime={isoDate}
                title={fullDate}
                className="text-[11px] text-[var(--text-secondary)]"
              >
                {timeLabel}
              </time>
            )}
            {notif.has_cta && notif.cta_label && (
              <span className="inline-flex items-center gap-0.5 text-xs font-bold text-[var(--brand-primary)]">
                {notif.cta_label}
                <ChevronRight size={12} aria-hidden="true" />
              </span>
            )}
          </span>
        </span>
      </button>

      {isUnread && (
        <button
          type="button"
          onClick={handleMarkReadOnly}
          className="w-11 min-h-[44px] shrink-0 flex items-center justify-center text-gray-400 hover:text-[var(--brand-primary)] hover:bg-gray-50 rounded-xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
          aria-label={t('cabin.notifications.mark_read_one')}
        >
          <Check size={16} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
